/**
 * tradie_software_chooser_controller.js — privacy-safe chooser analytics.
 *
 * The chooser itself remains a deterministic server-rendered GET form. This
 * controller only reports allowlisted interaction metadata to the existing
 * PostHog client; it never reads or captures criterion values, prices, or text.
 */

import { Controller } from "@hotwired/stimulus";

const CRITERIA = new Set([
    "region",
    "crew_size",
    "jobs_per_month",
    "billing",
    "max_monthly_budget",
    "workflow_depth",
    "web_importance",
    "ios_importance",
    "android_importance",
    "workflow_importance",
    "scheduling_importance",
    "job_costing_importance",
    "offline_importance",
    "xero_importance",
    "myob_importance",
    "team_management_importance",
]);

export default class extends Controller {
    static values = {
        blockers: Array,
        hasResults: Boolean,
        resultCount: Number,
        unknownCount: Number,
    };

    connect() {
        if (this.element.dataset.chooserAnalyticsTracked === "true") return;
        this.element.dataset.chooserAnalyticsTracked = "true";

        this._track("chooser_viewed");
        if (!this.hasResultsValue) return;

        this._track("results_generated", {
            result_count: this.resultCountValue,
            unknown_count: this.unknownCountValue,
        });
        if (this.resultCountValue === 0) {
            this._track("no_results", {
                blocking_criteria: this.blockersValue.filter((name) => CRITERIA.has(name)),
            });
        }
    }

    criteriaChanged(event) {
        const criterion = event.target?.name;
        if (CRITERIA.has(criterion)) {
            this._track("criteria_changed", { criterion });
        }
    }

    productOpened(event) {
        const link = event.currentTarget;
        this._track("product_result_opened", {
            product_slug: link.dataset.productSlug,
            rank: Number(link.dataset.resultRank),
        });
    }

    sourceOpened(event) {
        const link = event.currentTarget;
        const properties = {
            product_slug: link.dataset.productSlug,
            source_rank: Number(link.dataset.sourceRank),
        };
        this._track("source_opened", properties);
        if (properties.product_slug === "chippie") {
            this._track("matching_chippie_proof_opened", {
                source_rank: properties.source_rank,
            });
        }
    }

    accountStarted() {
        this._track("chooser_account_started");
    }

    _track(name, properties = {}) {
        if (typeof window.posthog !== "undefined") {
            window.posthog.capture(name, properties);
        }
    }
}
